import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Center,
  HStack,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import {
  ExportConversationIcon,
  CircleOutlineCheckIcon,
  PDFExportIcon,
  ZipExportIcon,
} from "modules/icons";
import colors from "app/colors";
import {
  MIXPANEL_EVENT_TYPE,
  useMixpanel,
} from "../providers/mixpanel-provider/mixpanel-provider";
import {
  CreateJobMutation,
  CreateJobMutationVariables,
  DeleteJobMutation,
  DeleteJobMutationVariables,
  JobQuery,
  JobQueryVariables,
  JobType,
  MessageThreadsQuery,
  MessageThreadsQueryVariables,
} from "gql/graphql";
import CREATE_JOB_MUTATION from "./networking/create-job";
import JOB_QUERY from "./networking/job";
import DELETE_JOB_MUTATION from "modules/drawers/networking/delete-job";
import { getMessageThreadTypeName } from "modules/utils/Functions";
import MESSAGE_THREADS_QUERY from "modules/messaging/_networking/message-threads";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";

enum DisplayType {
  CHOOSE = "CHOOSE",
  PROCESSING = "PROCESSING",
  DOWNLOAD = "DOWNLOAD",
}

type ExportType = "PDF" | "ZIP";

interface IProps {
  messageThreadId: string;
}

const ExportConversationModal = ({ messageThreadId }: IProps) => {
  const { closeModal, openDrawer } = useOverlay();
  const [displayType, setDisplayType] = useState<DisplayType>(
    DisplayType.CHOOSE,
  );
  const [exportType, setExportType] = useState<ExportType>("PDF");
  const [jobId, setJobId] = useState<string | null>();

  const { data } = useQuery<JobQuery, JobQueryVariables>(JOB_QUERY, {
    variables: {
      id: jobId!,
    },
    skip: !jobId,
  });

  const url = useMemo(() => data?.job?.url, [data]);

  const [createJobMutation] = useMutation<
    CreateJobMutation,
    CreateJobMutationVariables
  >(CREATE_JOB_MUTATION);

  const [deleteJobAPI] = useMutation<
    DeleteJobMutation,
    DeleteJobMutationVariables
  >(DELETE_JOB_MUTATION);

  const { data: messageThreadData } = useQuery<
    MessageThreadsQuery,
    MessageThreadsQueryVariables
  >(MESSAGE_THREADS_QUERY, {
    variables: {
      id: messageThreadId,
    },
    nextFetchPolicy: "cache-only",
  });

  const messageThread = useMemo(
    () => messageThreadData?.messageThreads[0],
    [messageThreadData],
  );

  const { track } = useMixpanel();

  const onCreateMessageThreadPdfClicked = useCallback(async () => {
    if (!messageThread) return;
    setDisplayType(DisplayType.PROCESSING);
    const response = await createJobMutation({
      variables: {
        messageThreadId,
        type:
          exportType === "PDF"
            ? JobType.MessageThreadPdf
            : JobType.MessageThreadZip,
      },
    });
    track(MIXPANEL_EVENT_TYPE.CONVERSTION_EXPORTED, {
      "File Type": exportType,
      "Conversation Type": getMessageThreadTypeName(messageThread),
    });
    if (response.data) {
      setJobId(response.data.createJob.id);
    }
  }, [createJobMutation, exportType, messageThread, messageThreadId, track]);

  const onDownloadClicked = useCallback(() => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.click();
      closeModal();
    }
  }, [closeModal, url]);

  const onDownloadsAndExportsClicked = useCallback(() => {
    openDrawer({
      type: DRAWER_TYPE.DOWNLOADS_EXPORTS,
    });
    closeModal();
  }, [closeModal, openDrawer]);

  const onCancelClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (jobId) {
        deleteJobAPI({
          variables: {
            id: jobId,
          },
        });
      }
      closeModal();
    },
    [closeModal, deleteJobAPI, jobId],
  );

  const onCloseClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  const onExportTypeChanged = useCallback((value: "PDF" | "ZIP") => {
    setExportType(value);
  }, []);

  useEffect(() => {
    if (url) setDisplayType(DisplayType.DOWNLOAD);
  }, [url]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16} p={4}>
        <ModalHeader>
          <Center>
            <VStack>
              {displayType === DisplayType.CHOOSE ? (
                <ExportConversationIcon boxSize={10} />
              ) : (
                <>
                  {exportType === "PDF" && (
                    <PDFExportIcon fill={colors.darkBlue} />
                  )}
                  {exportType === "ZIP" && <ZipExportIcon />}
                </>
              )}
              <Text>
                {exportType === "PDF" && "Export PDF"}
                {exportType === "ZIP" && "Export ZIP"}
              </Text>
            </VStack>
          </Center>
          {/*Confirmed that the modal close button cancels */}
          {!url && <ModalCloseButton onClick={onCancelClicked} />}
        </ModalHeader>
        <ModalBody p={0}>
          {displayType === DisplayType.CHOOSE && (
            <VStack spacing={0}>
              <Text fontSize={14}>Export this thread as:</Text>
              <RadioGroup
                w="full"
                value={exportType}
                onChange={onExportTypeChanged}
                pt={4}
              >
                <VStack alignItems="flex-start" w="full">
                  <Radio value="PDF">
                    <Text fontSize="sm">PDF</Text>
                  </Radio>
                  <Radio value="ZIP">
                    <Text fontSize="sm">ZIP</Text>
                  </Radio>
                </VStack>
              </RadioGroup>
              <VStack w="full" alignItems="flex-start" pl={6} spacing={0}>
                <Text fontSize={14}>Includes</Text>
                <UnorderedList pl={6}>
                  <ListItem>
                    <Text fontSize={14}>PDF of conversation</Text>
                  </ListItem>
                  <ListItem>
                    <Text fontSize={14}>All file attachments</Text>
                  </ListItem>
                </UnorderedList>
              </VStack>
              <HStack spacing={4} pt={6} pb={2}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={onCloseClicked}
                >
                  Cancel
                </Button>
                <Button
                  height={12}
                  w={28}
                  onClick={onCreateMessageThreadPdfClicked}
                >
                  Export
                </Button>
              </HStack>
            </VStack>
          )}
          {displayType === DisplayType.PROCESSING && (
            <VStack w="full" alignItems="center">
              <HStack>
                <Spinner size="sm" />
                <Text fontSize={14}>
                  {exportType} processing, please wait...
                </Text>
              </HStack>
              <HStack spacing={4} p={4}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={onCloseClicked}
                >
                  Close
                </Button>
                <Button height={12} w={28} isDisabled>
                  Download
                </Button>
              </HStack>
              <Text fontSize={14} color={colors.gray50} align="left">
                <Text fontWeight={700} as="span">
                  Don't want to wait?
                </Text>{" "}
                Find it later in
                <br />{" "}
                <Button
                  variant="link"
                  onClick={onDownloadsAndExportsClicked}
                  color={colors.blue}
                  fontSize={14}
                >
                  "Downloads & Exports"
                </Button>{" "}
                via the navigation bar
              </Text>
            </VStack>
          )}
          {displayType === DisplayType.DOWNLOAD && (
            <VStack w="full" alignItems="center">
              <Text fontSize={14} fontWeight={700}>
                <CircleOutlineCheckIcon
                  boxSize={4}
                  stroke={colors.greenPrimary}
                  mr={2}
                />
                {exportType} ready for download
              </Text>
              <HStack spacing={4} p={4}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={onCloseClicked}
                >
                  Close
                </Button>
                <Button height={12} w={28} onClick={onDownloadClicked}>
                  Download
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(ExportConversationModal);
